import {
  Box,
  Button,
  Text,
  Anchor,
  Flex,
  Modal,
  TextInput,
  Stack,
  Checkbox,
  Loader,
} from "@mantine/core";
import { IconCheck, IconX, IconSearch } from "@tabler/icons";
import { useState, useEffect } from "react";
import { showNotification } from "@mantine/notifications";
import { useAuth0 } from "@auth0/auth0-react";
import { assignRequestToNewIdea, getIdeaMatchesForEvidence, getSuggestionsForIdea } from "../../../../../Api";
import { useKnowledgeData } from "../../../../../hooks/useKnowledgeData";
import { useFields } from "../../../../../hooks/useFields";
import useApiSearch from "../../../../../hooks/useApiSearch";
import { SearchResultIdeaItem } from "../SearchResultIdeaItem";
import useEvidenceActions from "../../../../../hooks/useEvidenceActions";
import { useIdeaPage } from "../../../../../hooks/ideaPage";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";

interface AiGeneratedActionsProps {
  componentId: string;
  chosenEvidence: any;
  hoverCard?: boolean;
  setShowHoverCard?: (show: boolean) => void;
}

const AiGeneratedActions: React.FC<AiGeneratedActionsProps> = ({
  componentId,
  chosenEvidence,
  setShowHoverCard,
}) => {
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();
  const { allComponents } = useFields();
  const { defaultStatus, defaultPriority } = useKnowledgeData();
  const { refetchEvidences } = useIdeaPage();
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyMyIdeas, setOnlyMyIdeas] = useState(false);
  const [chosenIdea, setChosenIdea] = useState<any>(null);
  const [ideaMatches, setIdeaMatches] = useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const { searchIdeas, data, loading: searchLoading, resetResults } = useApiSearch();
  const { validate, isLoading: validateLoading } = useEvidenceActions({ evidenceId: chosenEvidence?._id });
  const userId = currentUser?.id;
  const ideasData = data?.ideas || [];
  const ideasLoading = searchLoading?.ideas || false;

  // Handle "Assign to a new idea" for generated ideas
  const handleAssignToNewIdea = () => {
    setShowHoverCard?.(false);
    const newIdeaObj = {
      title: chosenEvidence?.title,
      description: chosenEvidence?.text,
      componentId: componentId,
      ownerId: null,
      rolloutDate: null,
      status: defaultStatus,
      priority: defaultPriority,
    };

    const _body = {
      idea: newIdeaObj,
      evidence: { ...chosenEvidence },
    };

    // Check if a product area has been selected
    if (!componentId) {
      showNotification({
        title: "Product Area Missing",
        color: "red",
        message: "Please assign a product area to this request before creating a new idea.",
        icon: <IconX size="1.1rem" />,
        autoClose: 1200,
      });
      return;
    }

    // Find the product area by componentId
    const foundComponent = allComponents.find(
      (c) => c._id === componentId
    );

    // If no matching component is found, show an error
    if (!foundComponent) {
      showNotification({
        title: "Invalid Product Area",
        color: "red",
        message: "Please assign a product area to this request before creating a new idea.",
        icon: <IconX size="1.1rem" />,
        autoClose: 1200,
      });
      return;
    }

    // If a valid product area is found, assign its ownerId to the idea and evidence objects
    _body.idea.ownerId = foundComponent.ownerId;
    _body.evidence.ownerId = foundComponent.ownerId;
    _body.evidence.componentId = foundComponent._id;

    setLoadingAssign(true);

    // Submit the new idea request
    assignRequestToNewIdea(_body, auth0)
      .then((response) => {
        const ideaId = response.createNewIdeaRes?.idea?._id;

        showNotification({
          title: "Assigned Successfully!",
          color: "teal",
          message: (
            <Box>
              <Text>New idea has been created.</Text>
              {ideaId && (
                <Anchor href={`/idea/${ideaId}`} target="_blank">
                  Go to the new idea
                </Anchor>
              )}
            </Box>
          ),
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200,
        });
        refetchEvidences();

        if (ideaId) {
          setTimeout(() => {
            window.open(`/idea/${ideaId}`);
          }, 1300);
        } else {
          console.log("Did not receive an idea ID for the new idea", response);
        }
      })
      .catch((error) => {
        console.error("Error creating idea:", error);
        showNotification({
          title: "Error Creating Idea",
          color: "red",
          message: "An error occurred while creating the idea. Please try again.",
          icon: <IconX size="1.1rem" />,
          autoClose: 1200,
        });
      })
      .finally(() => {
        setLoadingAssign(false);
      });
  };

  const handleAssignToAnotherIdea = () => {
    setShowHoverCard?.(false);
    setModalOpened(true);
    setSearchQuery("");
    setChosenIdea(null);
    resetResults('ideas');
  };

  // Fetch ML suggestions when modal opens
  const getIdeasMatches = () => {
    if (!chosenEvidence?._id) return;
    
    setLoadingMatches(true);
    getIdeaMatchesForEvidence(chosenEvidence._id, auth0)
      .then((res: any) => {
        const filtered = res?.filter((m: any) => m.ideaId) || [];
        if (filtered.length === 0) {
          // If no matches, try getting suggestions based on title/text
          const title = chosenEvidence?.title || "";
          const text = chosenEvidence?.text || chosenEvidence?.description || "";
          if (title || text) {
            getSuggestionsForIdea(title, text, auth0)
              .then((suggestions: any) => {
                const suggestionMatches = suggestions?.ideaMatches || [];
                setIdeaMatches(suggestionMatches);
                setLoadingMatches(false);
              })
              .catch((e) => {
                console.error("Error fetching suggestions:", e);
                setLoadingMatches(false);
              });
          } else {
            setLoadingMatches(false);
          }
        } else {
          setIdeaMatches(filtered);
          setLoadingMatches(false);
        }
      })
      .catch((e) => {
        console.error("Error fetching idea matches:", e);
        // Fallback to suggestions if matches fail
        const title = chosenEvidence?.title || "";
        const text = chosenEvidence?.text || chosenEvidence?.description || "";
        if (title || text) {
          getSuggestionsForIdea(title, text, auth0)
            .then((suggestions: any) => {
              const suggestionMatches = suggestions?.ideaMatches || [];
              setIdeaMatches(suggestionMatches);
              setLoadingMatches(false);
            })
            .catch((e) => {
              console.error("Error fetching suggestions:", e);
              setLoadingMatches(false);
            });
        } else {
          setLoadingMatches(false);
        }
      });
  };

  // Search for ideas when query changes
  useEffect(() => {
    if (searchQuery !== "" && modalOpened) {
      // Clear ML matches when user starts searching
      setIdeaMatches([]);
      searchIdeas(searchQuery, onlyMyIdeas ? userId : undefined);
    } else if (searchQuery === "" && modalOpened) {
      // Reload ML matches when search is cleared
      getIdeasMatches();
    }
  }, [searchQuery, onlyMyIdeas, modalOpened, userId]);

  // Fetch ML suggestions when modal opens
  useEffect(() => {
    if (modalOpened && !searchQuery) {
      getIdeasMatches();
    }
  }, [modalOpened]);

  // Handle selecting an idea from search results
  const handleSelectIdea = (idea: any) => {
    const selectedIdea = idea?.idea || idea;
    setChosenIdea(selectedIdea);
  };

  // Handle assigning evidence to selected idea
  const handleAssignToSelectedIdea = async () => {
    if (!chosenIdea) {
      showNotification({
        title: "No Idea Selected",
        color: "red",
        message: "Please select an idea to assign this evidence to.",
        icon: <IconX size="1.1rem" />,
        autoClose: 2000,
      });
      return;
    }

    try {
      const result = await validate({
        ideaId: chosenIdea._id,
        sourceOfAssignment: "suggestion",
      });

      if (result.success) {
        showNotification({
          title: "Assigned Successfully!",
          color: "teal",
          message: (
            <Box>
              <Text>Evidence has been assigned to the idea.</Text>
              <Anchor href={`/idea/${chosenIdea._id}`} target="_blank">
                Go to the idea
              </Anchor>
            </Box>
          ),
          icon: <IconCheck size="1.1rem" />,
          autoClose: 2000,
        });

        refetchEvidences();
        setModalOpened(false);
        setChosenIdea(null);
        setSearchQuery("");

        // Open the idea page
        setTimeout(() => {
          window.open(`/idea/${chosenIdea._id}`);
        }, 1300);
      }
    } catch (error: any) {
      console.error("Error assigning evidence:", error);
      showNotification({
        title: "Error Assigning Evidence",
        color: "red",
        message: error?.message || "An error occurred while assigning the evidence. Please try again.",
        icon: <IconX size="1.1rem" />,
        autoClose: 2000,
      });
    }
  };

  return (
    <>
      <Flex justify="flex-end" w="100%">
        <Button
          fz="12px"
          color="indigo"
          w="135px"
          mr="10px"
          styles={(theme) => ({
            root: {
              height: "32px",
              backgroundColor: "#5C5CEB",
              border: 0,
              fontWeight: 400,
              padding: 0,
              "&:hover": {
                backgroundColor: theme.fn.darken("#5C5CEB", 0.05),
              },
            },
          })}
          onClick={handleAssignToNewIdea}
          loading={loadingAssign}
        >
          <Text fw={500}>Assign to a new idea</Text>
        </Button>
        <Button
          fz="12px"
          color="indigo"
          w="135px"
          mr="15px"
          styles={(theme) => ({
            root: {
              height: "32px",
              backgroundColor: "#5C5CEB",
              border: 0,
              fontWeight: 400,
              padding: 0,
              "&:hover": {
                backgroundColor: theme.fn.darken("#5C5CEB", 0.05),
              },
            },
          })}
          onClick={handleAssignToAnotherIdea}
        >
          <Text fw={500}>Assign to another idea</Text>
        </Button>
      </Flex>

      {/* Modal for searching and selecting an existing idea */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setSearchQuery("");
          setChosenIdea(null);
          setIdeaMatches([]);
          resetResults('ideas');
        }}
        title={
          <Text fz={20} fw={600}>
            Assign to another idea
          </Text>
        }
        size="lg"
        yOffset={100}
      >
        <Stack spacing="md">
          <TextInput
            placeholder="Search for an idea..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            icon={<IconSearch size={16} />}
            rightSection={ideasLoading && <Loader size="xs" />}
          />

          <Checkbox
            label="Only my ideas"
            checked={onlyMyIdeas}
            onChange={(e) => setOnlyMyIdeas(e.currentTarget.checked)}
          />

          <Box style={{ maxHeight: "400px", overflowY: "auto" }}>
            {searchQuery && ideasData && ideasData.length > 0 ? (
              <Stack spacing="xs">
                {ideasData.map((idea: any) => (
                  <SearchResultIdeaItem
                    key={idea._id}
                    ideaItem={idea}
                    loading={false}
                    chooseIdea={handleSelectIdea}
                    chosenIdea={chosenIdea}
                  />
                ))}
              </Stack>
            ) : searchQuery && !ideasLoading && ideasData && ideasData.length === 0 ? (
              <Text size="sm" color="dimmed" ta="center" py="xl">
                No ideas found. Try a different search term.
              </Text>
            ) : !searchQuery ? (
              <>
                {loadingMatches ? (
                  <Flex justify="center" py="xl">
                    <Loader size="sm" />
                  </Flex>
                ) : ideaMatches && ideaMatches.length > 0 ? (
                  <Stack spacing="xs">
                    <Text size="xs" mb="xs" fw={500}>
                      Suggested by Bagel
                    </Text>
                    {ideaMatches.map((ideaMatch: any) => {
                      const idea = ideaMatch?.idea || ideaMatch;
                      return (
                        <SearchResultIdeaItem
                          key={idea?._id || ideaMatch?.ideaId}
                          ideaItem={idea}
                          loading={false}
                          chooseIdea={handleSelectIdea}
                          chosenIdea={chosenIdea}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Text size="sm" color="dimmed" ta="center" py="xl">
                    No suggestions available. Enter a search term to find ideas.
                  </Text>
                )}
              </>
            ) : null}
          </Box>

          {chosenIdea && (
            <Button
              fullWidth
              onClick={handleAssignToSelectedIdea}
              loading={validateLoading.validate}
              styles={(theme) => ({
                root: {
                  height: "32px",
                  backgroundColor: "#5C5CEB",
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#5C5CEB", 0.05),
                  },
                },
              })}
            >
              <Text fw={500}>Assign to "{chosenIdea.title}"</Text>
            </Button>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default AiGeneratedActions;

